import React from "react";
import {
    Area,
    AreaChart,
    Brush,
    CartesianGrid,
    Legend,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import styled from "styled-components";
import { Stack } from "@mui/material";
import { format, isValid } from "date-fns";
import NoDataPlaceholder from "src/components/no-data-placeholder";
import JointChartDataSkeletonHelper from "src/components/joint-chart-data-skeleton-helper";
import ChartInfoBox from "src/scenes/overview/overview-components/chart-info-box";
import ZeroAccuracyPlaceholder from "src/scenes/overview/overview-components/zero-accuracy-placeholder";

const TooltipContainer = styled.div`
    background-color: ${({ theme }) => theme.palette.background.tooltip};
    padding: 0.5rem;
    min-width: 10rem;
    align-items: center;
    flex-direction: column;
    border-radius: 6px;
`;

const TooltipTitle = styled.p`
    margin-top: 0;
    margin-bottom: 0.5rem;
`;

const TooltipContent = styled.p`
    font-size: 12px;
    margin: 0;
    color: ${({ color }) => color};
`;

const CustomTooltip = ({ active, payload, isExpanded }) => {
    if (active && payload && payload.length) {
        return (
            <TooltipContainer role="status" aria-live="assertive">
                <TooltipTitle>
                    {isValid(new Date(payload[0].payload.datetime)) &&
                        `${format(new Date(payload[0].payload.datetime), "EEE d MMM")}`}
                </TooltipTitle>
                {payload.map((entry) => {
                    return (
                        <Stack key={entry.name} direction="row" spacing={0.5}>
                            <TooltipContent color={entry.fill}>{entry.name}</TooltipContent>
                            <TooltipContent>{entry.value}</TooltipContent>
                        </Stack>
                    );
                })}
                {isExpanded && (
                    <Stack
                        direction="column"
                        mt={1}
                        style={{ width: "100%", backgroundColor: "#303030", borderRadius: "8px" }}
                    >
                        <TooltipContent style={{ fontWeight: 300, margin: "8px" }}>
                            Click for details
                        </TooltipContent>
                    </Stack>
                )}
            </TooltipContainer>
        );
    }
    return null;
};
const CreditExpeditureBacklogChart = ({ data, status, isExpanded, accuracy }) => {
    if (!data) return <NoDataPlaceholder variant="overview" />;

    if (
        status === "STREAMING" &&
        !["backlog", "bugsBacklog"].every((key) => Object.keys(data).includes(key))
    )
        return <JointChartDataSkeletonHelper />;
    if (
        status === "COMPLETE" &&
        !["backlog", "bugsBacklog"].every((key) => Object.keys(data).includes(key))
    )
        return <NoDataPlaceholder variant="overview" />;

    const [activeSeries, setActiveSeries] = React.useState([]);
    const [clickedPoint, setClickedPoint] = React.useState(null);

    const handleClick = (e) => {
        setClickedPoint(e);
    };

    if (accuracy && accuracy.level === "zero")
        return <ZeroAccuracyPlaceholder dependencies={accuracy.dependencies} />;
    const handleLegendClick = (dataKey) => {
        if (activeSeries.includes(dataKey)) {
            setActiveSeries(activeSeries.filter((el) => el !== dataKey));
        } else {
            setActiveSeries((prev) => [...prev, dataKey]);
        }
    };

    const flattenExpenditureData = () => {
        const { backlog: backlogData, bugsBacklog: bugsBacklogData } = data;

        const flattenedDataMap = {};

        (backlogData || []).forEach((item) => {
            const { datetime, backlog, prodBacklog } = item;
            if (!flattenedDataMap[datetime]) {
                flattenedDataMap[datetime] = { datetime };
            }
            flattenedDataMap[datetime].backlog = backlog;
            flattenedDataMap[datetime].prodBacklog = prodBacklog;
        });

        (bugsBacklogData || []).forEach((item) => {
            const { datetime, bugsBacklog, prodBugsBacklog } = item;
            if (!flattenedDataMap[datetime]) {
                flattenedDataMap[datetime] = { datetime };
            }
            flattenedDataMap[datetime].bugsBacklog = bugsBacklog;
            flattenedDataMap[datetime].prodBugsBacklog = prodBugsBacklog;
        });
        return Object.values(flattenedDataMap);
    };

    const flatExpenditureData = flattenExpenditureData();

    return (
        <div style={{ width: "100%" }}>
            <ResponsiveContainer width="100%" height={isExpanded ? 400 : 200}>
                <AreaChart
                    accessibilityLayer
                    data={flatExpenditureData.map(
                        ({ datetime, backlog, prodBacklog, bugsBacklog, prodBugsBacklog }) => ({
                            datetime,
                            backlog,
                            prodBacklog,
                            bugsBacklog,
                            prodBugsBacklog,
                            date:
                                isValid(new Date(datetime)) && format(new Date(datetime), "d MMM"),
                        })
                    )}
                    syncId={isExpanded ? null : "mainAnalyticsSync"}
                    syncMethod="value"
                    onClick={(e) => handleClick(e)}
                    margin={{
                        top: 25,
                        right: 50,
                        left: 0,
                        bottom: 15,
                    }}
                >
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis
                        dataKey="date"
                        tick={{
                            fill: "#EAF0F4",
                            opacity: "0.3",
                            fontSize: "11px",
                            fontWeight: "300",
                        }}
                    />
                    <YAxis
                        tick={{
                            fill: "#EAF0F4",
                            opacity: "0.3",
                            fontSize: "11px",
                            fontWeight: "300",
                        }}
                    />
                    <Tooltip
                        cursor={{ opacity: "0.2" }}
                        content={<CustomTooltip isExpanded={isExpanded} />}
                    />
                    <Area
                        dataKey="backlog"
                        fill="#8884d8"
                        stroke="#8884d8"
                        name="Issues in backlog"
                        hide={activeSeries.includes("backlog")}
                    />
                    <Area
                        dataKey="bugsBacklog"
                        fill="#82ca9d"
                        stroke="#82ca9d"
                        style={{ opacity: 0.8 }}
                        name="Bug issues in backlog"
                        hide={activeSeries.includes("bugsBacklog")}
                    />
                    <Area
                        dataKey="prodBacklog"
                        fill="#ffc658"
                        stroke="#ffc658"
                        style={{ opacity: 0.6 }}
                        name="Prod issues in backlog"
                    />
                    <Area
                        dataKey="prodBugsBacklog"
                        fill="#e76f51"
                        stroke="#e76f51"
                        style={{ opacity: 0.4 }}
                        name="Prod Bug issues in backlog"
                    />
                    {!isExpanded && (
                        <Brush
                            height={15}
                            stroke="#333"
                            fill="#444"
                            travellerWidth={10}
                            travellerStroke="#555"
                            travellerFill="#666"
                        />
                    )}
                    {clickedPoint !== null && isExpanded && (
                        <ReferenceLine x={clickedPoint.activeLabel} stroke="#5ac36b" />
                    )}
                    {isExpanded && (
                        <Legend
                            verticalAlign="bottom"
                            height={22}
                            wrapperStyle={{ fontSize: "11px", bottom: "6px" }}
                            onClick={(props) => handleLegendClick(props.dataKey)}
                        />
                    )}
                </AreaChart>
            </ResponsiveContainer>
            {isExpanded && clickedPoint && clickedPoint.activePayload && (
                <ChartInfoBox clickedPoint={clickedPoint} type="backlog" />
            )}
        </div>
    );
};

export default CreditExpeditureBacklogChart;
